import { createCreditorMessageTool, } from '@debbie/common/dist/agents/tools/create-creditor-message.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { toEditorMentions } from '@debbie/common/dist/templates/suggestions.js';
import { looksLikeHtml, plaintextToHtml, } from '@debbie/common/dist/util/html.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import { useTenantLoadable } from '@debbie/cortex/dist/tenant/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { encodeText, parseText } from '../../utils/templates.js';
import NewEditor from '../editor/Editor.js';
import Errors from '../Errors.js';
import File from '../File/index.js';
import FileDropzone from '../FileDropzone.js';
import ClientNoteRecipients from '../interactions/ClientNoteRecipients.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import { AddAttachmentChip, AttachmentList, RenderedHtmlBody, attachmentFile, useCaseFiles, useHandleToLabel, } from './CreateInteractionBody.js';
import { EditError, Section, SectionLabel, useProposalEdits, } from './shared.js';
const BodyBox = styled.div `
  background: #fff;
  border: 1px solid ${THEME.BORDER};
  border-radius: 3px;
  padding: 12px 14px;
  line-height: 1.6;
  max-height: 400px;
  overflow: auto;
  color: ${THEME.COLOR_PRIMARY};
`;
const AttachmentEditor = styled.div `
  margin-top: 10px;
`;
// A proposed body may be plaintext; render and seed it as HTML so paragraph
// structure survives, matching CreateInteractionBody and the server's apply.
const toHtmlBody = (body) => looksLikeHtml(body) ? body : plaintextToHtml(body);
const EditableCreditorMessage = ({ input, caseId, creditorId, caseGroupId, defaultRecipientIds, onEditsChange, }) => {
    const lang = useSelector((s) => s.lang);
    const mentions = useMentionSuggestions(TemplateType.CREDITOR_MESSAGE, caseGroupId);
    const bodyBaseline = useRef(null);
    const baselineRecipientIds = input.recipientIds ?? defaultRecipientIds;
    const [recipientIds, setRecipientIds] = useState(baselineRecipientIds);
    const [attachments, setAttachments] = useState(input.attachments ?? []);
    const attachmentsRef = useRef(attachments);
    attachmentsRef.current = attachments;
    const [uploadedById, setUploadedById] = useState(new Map());
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const dropzoneRef = useRef(null);
    const caseFiles = useCaseFiles(caseId);
    const { patch, editError, emit } = useProposalEdits(createCreditorMessageTool, input, onEditsChange);
    const onBodyChange = (html) => {
        const value = parseText(html, mentions);
        if (bodyBaseline.current == null) {
            bodyBaseline.current = value;
            return;
        }
        if (value === bodyBaseline.current) {
            delete patch.current.body;
        }
        else {
            patch.current.body = value;
        }
        emit();
    };
    const onRecipientsChange = (next) => {
        setRecipientIds(next);
        const proposed = input.recipientIds;
        const isEdit = proposed == null
            ? next.length > 0
            : [...next].sort().join(',') !== [...proposed].sort().join(',');
        if (isEdit) {
            patch.current.recipientIds = next;
        }
        else {
            delete patch.current.recipientIds;
        }
        emit();
    };
    const onAttachmentsChange = (next) => {
        setAttachments(next);
        const baseline = (input.attachments ?? [])
            .map(({ fileId }) => fileId)
            .sort()
            .join(',');
        const current = next
            .map(({ fileId }) => fileId)
            .sort()
            .join(',');
        if (current === baseline) {
            delete patch.current.attachments;
        }
        else {
            patch.current.attachments = next;
        }
        emit();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ClientNoteRecipients, { creditorId: creditorId, selectedIds: recipientIds, onChange: onRecipientsChange }),
        React.createElement(NewEditor, { mentions: toEditorMentions(mentions), mentionSuggestions: mentions, initialContent: encodeText(toHtmlBody(input.body), mentions), onChange: onBodyChange }),
        React.createElement(AttachmentEditor, null,
            React.createElement(FileDropzone, { dropzoneRef: dropzoneRef, setLoading: (loading) => {
                    setUploadingAttachment(loading);
                    if (loading) {
                        const edits = { ...patch.current };
                        onEditsChange({
                            edits: Object.keys(edits).length > 0 ? edits : null,
                            isValid: false,
                        });
                    }
                    else {
                        emit();
                    }
                }, onChange: (file) => {
                    setUploadedById((previous) => new Map(previous).set(file.fileId, {
                        fileId: file.fileId,
                        fileName: file.fileName ?? `${file.fileId.slice(0, 8)}…`,
                        contentType: file.contentType ?? '',
                    }));
                    onAttachmentsChange([
                        ...attachmentsRef.current,
                        { fileId: file.fileId },
                    ]);
                } },
                React.createElement("div", null,
                    attachments.map(({ fileId }) => (React.createElement(File, { key: fileId, file: uploadedById.get(fileId) ??
                            attachmentFile(caseFiles.byId, fileId), removable: !uploadingAttachment, onRemove: () => onAttachmentsChange(attachments.filter((attachment) => attachment.fileId !== fileId)) }))),
                    React.createElement(AddAttachmentChip, { $disabled: uploadingAttachment, onClick: () => {
                            if (!uploadingAttachment) {
                                dropzoneRef.current?.open();
                            }
                        } },
                        "+ ",
                        lang.interactions.addFiles)))),
        caseFiles.errors.length > 0 && React.createElement(Errors, { errors: caseFiles.errors }),
        editError && (React.createElement(EditError, null,
            lang.agentProposals.invalidEdits,
            ": ",
            editError))));
};
const CreateCreditorMessageBody = ({ input, caseId, onEditsChange }) => {
    const lang = useSelector((s) => s.lang);
    const handleToLabel = useHandleToLabel(caseId);
    const caseLoadable = useCase(onEditsChange ? caseId : undefined);
    const singleCase = h.hasData(caseLoadable) ? caseLoadable.data : null;
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const tenantLoadable = useTenantLoadable();
    const tenant = h.hasData(tenantLoadable) ? tenantLoadable.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    if (onEditsChange) {
        const errors = [
            h.isError(caseLoadable) ? caseLoadable.error.message : null,
            h.isError(creditorLoadable) ? creditorLoadable.error.message : null,
            h.isError(propertyTypesLoadable)
                ? propertyTypesLoadable.error.message
                : null,
            h.isError(tenantLoadable) ? tenantLoadable.error.message : null,
        ].filter((message) => message != null);
        if (errors.length > 0) {
            return React.createElement(Errors, { errors: errors });
        }
        // Mounting before these settle would later re-seed the editor (the mention
        // suggestions change once tenant/property types load) and discard the
        // reviewer's typing — same guard as CreateInteractionBody.
        if (singleCase == null ||
            creditor == null ||
            tenant == null ||
            !h.hasData(propertyTypesLoadable)) {
            return React.createElement(PlaceholderLoader, null);
        }
        return (React.createElement(EditableCreditorMessage, { input: input, caseId: caseId, creditorId: creditor.creditorId, caseGroupId: singleCase.caseGroup?.id ?? null, defaultRecipientIds: creditor.contactId == null ? [] : [creditor.contactId], onEditsChange: onEditsChange }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Section, null,
            React.createElement(SectionLabel, null, lang.agentProposalBodies.creditorMessage),
            React.createElement(BodyBox, null,
                React.createElement(RenderedHtmlBody, { html: toHtmlBody(input.body), handleToLabel: handleToLabel }))),
        input.recipientIds?.length === 0 && (React.createElement(Section, null, lang.interactions.noRecipientsSelected)),
        input.attachments?.length ? (React.createElement(AttachmentList, { caseId: caseId, attachments: input.attachments })) : null));
};
export default CreateCreditorMessageBody;
//# sourceMappingURL=CreateCreditorMessageBody.js.map